import {Timestamp} from 'firebase/firestore';
import moment from 'moment';
import {memo, useCallback, useMemo, useState} from 'react';
import {Api} from '../../api';
import {useAppDispatch, useAppSelector, useI18n} from '../../hooks';
import {useStorageImageUrl} from '../../hooks/api/useStorageImageUrl';
import {EventDTO} from '../../models/event/EventDTO';
import {EventActions} from '../../store/events/EventActions';
import {GlobalActions} from '../../store/global/GlobalActions';
import {UserSelector} from '../../store/user/UserSelector';
import {CompanySelector} from '../../store/company/CompanySelector';
import {AttendeeRack} from '../attendee-rack/AttendeeRack';
import {
  ConfirmationModal,
  DevToolFloatingId,
  QuestionsModal,
  SecondaryButton,
  TextHeading5,
  TextHeading6,
  UserListModal,
} from '../index';
import {EvaluationModal} from '../../scenes/event/components/right-panel/components/evaluation-modal/EvaluationModal';
import {EventListCardDeadline} from '../event-card/components/deadline/EventListCardDeadline';
import {CalendarConnectionNudgeModal} from '../calendar/CalendarConnectionNudgeModal';
import {hasUserRespondedEventEvaluation, CalendarProvider} from 'hubbl-shared';
import {
  CardContainer,
  CardCoverImage,
  CardContent,
  CardTitle,
  CardDetails,
  CardFooter,
  CardAttendeesInfo,
  CardActionButtons,
  CardStatusIndicator,
  StatusBadge,
  formatEventDate,
  formatEventTime,
} from './shared';

interface EventCardProps {
  event: EventDTO;
  isPast?: boolean;
  onClick: () => void;
}

export const EventCard = memo(
  ({event, isPast = false, onClick}: EventCardProps) => {
    const dispatch = useAppDispatch();
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const companyDetails = useAppSelector(CompanySelector.getCompanyDetails);
    const t = useI18n();
    const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);
    const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
    const [isCalendarNudgeModalOpen, setIsCalendarNudgeModalOpen] =
      useState(false);

    // Check if user has calendar integration
    const hasCalendarIntegration = !!userDetails?.settings?.calendarIntegration;

    // Check if company has a default calendar provider
    const defaultCalendarProvider =
      companyDetails?.settings.defaultCalendarProvider;

    // Determine if we should show calendar connection nudge
    const shouldShowCalendarNudge =
      !!defaultCalendarProvider && !hasCalendarIntegration;

    const isUserAttending = (event: any) => {
      if (!userDetails || !event.attendees) return false;
      return event.attendees.some(
        (attendee: any) => attendee.id === userDetails.id,
      );
    };

    const attending = isUserAttending(event);

    const isUserDeclined = useMemo(() => {
      if (!userDetails) return false;
      return event.declinedUserIds?.includes(userDetails.id) || false;
    }, [event.declinedUserIds, userDetails]);

    // Check if event has passed
    const isEventPassed = useMemo(() => {
      return moment(
        event.endDateTime?.toDate() || event.startDateTime.toDate(),
      ).isBefore(moment());
    }, [event.endDateTime, event.startDateTime]);

    // Check if deadline has passed
    const isDeadlinePassed = useMemo(() => {
      if (!event.deadline) return false;
      return moment(event.deadline.toDate()).isBefore(moment());
    }, [event.deadline]);

    // Check if user has responded to evaluation
    const hasUserEvaluated = useMemo(() => {
      if (!userDetails || !event.evaluation) return false;
      // Convert the evaluation to match the expected type
      const evaluation = {
        ...event.evaluation,
        endDate: event.evaluation.endDate.toDate().toISOString(),
      };
      return hasUserRespondedEventEvaluation(evaluation, userDetails.id);
    }, [event.evaluation, userDetails]);

    // Check if there's an active evaluation that user hasn't completed
    const hasActiveEvaluation = useMemo(() => {
      if (!event.evaluation || !attending || hasUserEvaluated) return false;
      const evaluationEndDate = moment(event.evaluation.endDate.toDate());
      return evaluationEndDate.isAfter(moment());
    }, [event.evaluation, attending, hasUserEvaluated]);

    const coverImageUrl = useStorageImageUrl(event.coverImageUrl) || null;

    // Core join event logic (without calendar nudge check)
    const proceedWithJoinEvent = useCallback(async () => {
      if (event.questions && event.questions.length > 0) {
        setIsQuestionsModalOpen(true);
        return;
      }

      dispatch(GlobalActions.setIsBusy(true));

      try {
        if (!userDetails) {
          throw new Error('User details not found');
        }

        await Api.event.joinEvent(event.companyId, event.id, userDetails);
        // Refresh the event data after joining
        const updatedEvent = await Api.event.getEventById(
          event.companyId,
          event.id,
        );
        if (updatedEvent) {
          // Trigger a page refresh to update the UI
          dispatch(EventActions.updateEvent(updatedEvent));
        }
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, userDetails, event.companyId, event.id, event.questions]);

    const onJoinEvent = useCallback(async () => {
      // Show calendar nudge modal if applicable
      if (shouldShowCalendarNudge) {
        setIsCalendarNudgeModalOpen(true);
        return;
      }

      // Otherwise proceed normally
      await proceedWithJoinEvent();
    }, [shouldShowCalendarNudge, proceedWithJoinEvent]);

    const onDeclineEvent = useCallback(async () => {
      dispatch(GlobalActions.setIsBusy(true));

      try {
        if (!userDetails) {
          throw new Error('User details not found');
        }

        await Api.event.declineEvent(event.companyId, event.id, {
          declinedAt: moment().format(),
          user: {
            firstName: userDetails.firstName,
            id: userDetails.id,
            fullName: userDetails.fullName,
            photoUrl: userDetails.photoUrl,
            email: userDetails.email,
            department: userDetails.department,
          },
        });

        // Refresh the event data after declining
        const updatedEvent = await Api.event.getEventById(
          event.companyId,
          event.id,
        );
        if (updatedEvent) {
          // Trigger a page refresh to update the UI
          dispatch(EventActions.updateEvent(updatedEvent));
        }
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, event.companyId, event.id, userDetails]);

    const onUndeclineEvent = useCallback(async () => {
      if (!userDetails) return;

      dispatch(GlobalActions.setIsBusy(true));

      try {
        await Api.event.undeclineEvent(
          event.companyId,
          event.id,
          userDetails.id,
        );
        // Refresh the event data after undececlining
        const updatedEvent = await Api.event.getEventById(
          event.companyId,
          event.id,
        );
        if (updatedEvent) {
          // Trigger a page refresh to update the UI
          dispatch(EventActions.updateEvent(updatedEvent));
        }
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    }, [dispatch, event.companyId, event.id, userDetails]);

    const handleQuestionsSuccess = useCallback(async () => {
      // Refresh event data after questions are submitted
      const updatedEvent = await Api.event.getEventById(
        event.companyId,
        event.id,
      );
      if (updatedEvent) {
        // Trigger a page refresh to update the UI
        dispatch(EventActions.updateEvent(updatedEvent));
      }
    }, [event.companyId, event.id, dispatch]);

    const handleDeclineClick = useCallback(() => {
      setIsDeclineModalOpen(true);
    }, []);

    const handleDeclineConfirm = useCallback(async () => {
      setIsDeclineModalOpen(false);
      await onDeclineEvent();
    }, [onDeclineEvent]);

    const handleDeclineCancel = useCallback(() => {
      setIsDeclineModalOpen(false);
    }, []);

    const handleAttendeesClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setIsAttendeesModalOpen(true);
    }, []);

    const handleReviewEventClick = useCallback(() => {
      console.log('handleReviewEventClick called');
      setIsEvaluationModalOpen(true);
    }, []);

    const handleEvaluationSuccess = useCallback(async () => {
      // Refresh event data after evaluation is submitted
      const updatedEvent = await Api.event.getEventById(
        event.companyId,
        event.id,
      );
      if (updatedEvent) {
        dispatch(EventActions.updateEvent(updatedEvent));
      }
    }, [event.companyId, event.id, dispatch]);

    const handleCalendarNudgeClose = useCallback(() => {
      setIsCalendarNudgeModalOpen(false);
    }, []);

    const handleContinueWithoutConnection = useCallback(async () => {
      setIsCalendarNudgeModalOpen(false);
      // Continue with the normal join flow
      await proceedWithJoinEvent();
    }, [proceedWithJoinEvent]);

    return (
      <>
        <CardContainer
          onClick={onClick}
          isPast={isPast || isUserDeclined}
          hasActiveEvaluation={hasActiveEvaluation}
          isDeclined={isUserDeclined}
          variant="event">
          <EventListCardDeadline event={event} isPast={isPast} />

          {coverImageUrl && <CardCoverImage src={coverImageUrl} alt="" />}

          <CardContent>
            <CardTitle>
              {`${event.eventTitle.en} ${event.eventEmoji}`}
            </CardTitle>

            <CardDetails>
              <TextHeading6
                text={`📅 ${formatEventDate(event.startDateTime)}`}
              />
              <TextHeading6
                text={`🕐 ${formatEventTime(event.startDateTime)} ${event.endDateTime ? `- ${formatEventTime(event.endDateTime)}` : ''}`}
              />

              {event.eventLocation && (
                <TextHeading6 text={`📍 ${event.eventLocation}`} />
              )}
            </CardDetails>

            <CardFooter>
              <CardAttendeesInfo onClick={handleAttendeesClick}>
                <AttendeeRack
                  disableEmptyState
                  truncateAt={5}
                  users={event.attendees || []}
                />
                <TextHeading5
                  text={
                    isPast
                      ? `${event.attendees?.length || 0} ${t.events.colleaguesAttended}`
                      : `${event.attendees?.length || 0} ${t.events.colleaguesAttending}`
                  }
                />
              </CardAttendeesInfo>

              <CardStatusIndicator>
                {/* Show decline option for initial state */}
                {!attending &&
                  !isUserDeclined &&
                  !isEventPassed &&
                  !isDeadlinePassed &&
                  !hasActiveEvaluation && (
                    <SecondaryButton
                      isCompact
                      customWidth={100}
                      label={t.events.declineEvent}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleDeclineClick();
                      }}
                    />
                  )}
                <StatusBadge
                  status={
                    attending
                      ? 'attending'
                      : isUserDeclined
                        ? 'declined'
                        : hasActiveEvaluation
                          ? 'action-required'
                          : isEventPassed
                            ? 'none'
                            : isDeadlinePassed
                              ? 'none'
                              : 'action-required'
                  }
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();

                    if (isUserDeclined) {
                      onUndeclineEvent();
                    } else if (hasActiveEvaluation) {
                      handleReviewEventClick();
                    } else if (attending) {
                      // No action for attending events
                    } else if (isDeadlinePassed) {
                      // No action when deadline has passed
                    } else {
                      onJoinEvent();
                    }
                  }}
                  isClickable={(() => {
                    const clickable =
                      (!isEventPassed && !attending && !isDeadlinePassed) ||
                      (isEventPassed &&
                        hasActiveEvaluation &&
                        !hasUserEvaluated);

                    return clickable;
                  })()}>
                  {attending
                    ? isEventPassed
                      ? hasUserEvaluated
                        ? t.events.attendedThankYou
                        : hasActiveEvaluation
                          ? t.events.reviewRequired
                          : t.events.attended
                      : t.events.attending
                    : isUserDeclined
                      ? t.events.declinedClickToChange
                      : hasActiveEvaluation
                        ? t.events.reviewRequired
                        : isEventPassed
                          ? t.events.eventPassed
                          : isDeadlinePassed
                            ? t.events.deadlinePassed
                            : t.events.join}
                </StatusBadge>
              </CardStatusIndicator>
            </CardFooter>
          </CardContent>
          <DevToolFloatingId id={event.id} />
        </CardContainer>

        <ConfirmationModal
          isOpen={isDeclineModalOpen}
          onClose={handleDeclineCancel}
          onConfirm={handleDeclineConfirm}
          title={t.events.declineEvent}
          message={t.events.declineEventMessage}
          confirmLabel={t.events.declineEvent}
          cancelLabel={t.events.keepInvitation}
        />

        {event.questions && event.questions.length > 0 && userDetails && (
          <QuestionsModal
            isOpen={isQuestionsModalOpen}
            onClose={() => setIsQuestionsModalOpen(false)}
            eventId={event.id}
            companyId={event.companyId}
            questions={event.questions}
            onSuccess={handleQuestionsSuccess}
            user={userDetails}
          />
        )}

        <UserListModal
          isOpen={isAttendeesModalOpen}
          onClose={() => setIsAttendeesModalOpen(false)}
          title={`${t.events.attendees} (${event.attendees?.length || 0})`}
          users={event.attendees || []}
        />

        {event.evaluation &&
          event.evaluation.questions &&
          event.evaluation.questions.length > 0 &&
          userDetails && (
            <EvaluationModal
              evaluationCustomDescription={event.evaluation.customDescription}
              isOpen={isEvaluationModalOpen}
              onClose={() => setIsEvaluationModalOpen(false)}
              eventId={event.id}
              eventTitle={`${event.eventTitle[userDetails?.settings.preferredLanguage || 'en']} ${event.eventEmoji}`}
              companyId={event.companyId}
              questions={event.evaluation.questions}
              onSuccess={handleEvaluationSuccess}
            />
          )}

        {defaultCalendarProvider && (
          <CalendarConnectionNudgeModal
            isOpen={isCalendarNudgeModalOpen}
            onClose={handleCalendarNudgeClose}
            onContinueWithoutConnection={handleContinueWithoutConnection}
            onSuccessfulConnection={proceedWithJoinEvent}
            provider={defaultCalendarProvider}
          />
        )}
      </>
    );
  },
);
